import { useEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Location } from '../../../clients/quipli-service/location/location';
import { SelectForm } from '../../../components/select-form/select-form';
import useLocalStorage from 'use-local-storage';
import { Props } from 'react-select';

export const LocationSelect = ({
  orderLocation,
  setLocation,
  setRootAttributes,
  locations,
  dataCy,
  isEditMode = true,
  clearable = false,
  dropdownProps,
  restoreLast = true,
}: {
  orderLocation?: Location | null | undefined;
  setLocation?: (l: Location | null) => void;
  setRootAttributes?: (o: any) => undefined;
  locations?: Location[];
  dataCy?: string;
  isEditMode?: boolean;
  clearable?: boolean;
  dropdownProps?: Props;
  restoreLast?: boolean;
}) => {
  const { listLocations } = useAPI();

  // State
  const [isLoading, setLoader] = useState<boolean>(false);
  const [list, setList] = useState<Location[]>([]);
  const [value, setValue] = useState<Location | null | undefined>(orderLocation);
  const [lastLocationId, setLastLocationId] = useLocalStorage<number>('lastLocationId', 0);

  const updateValueAndStorage = (location: Location | null) => {
    setValue(location);
    location && setLastLocationId(location.id);
  };

  // Effects
  useEffect(() => {
    const getList = async () => {
      setLoader(true);

      // If locations are provided, don't fetch them
      const locationsList = locations || (await listLocations(true));
      setList(locationsList);

      if (locationsList.length && !value) {
        let location: Location | undefined;

        // Check if a order location id was provided
        if (orderLocation) {
          location = locationsList.find((l) => l.id === orderLocation.id);

          if (location) {
            setValue(location);
          }
        } else {
          // Otherwise check if a location id is saved locally
          location = locationsList.find((l) => l.id === lastLocationId);
          if (restoreLast) {
            if (location) setValue(location);
            else updateValueAndStorage(locationsList[0]);
          }
        }
      }
      setLoader(false);
    };

    getList();
  }, []);

  useEffect(() => {
    value !== undefined && setLocation && setLocation(value);
    setRootAttributes &&
      setRootAttributes({
        'data-selected-location-id': value?.id || '',
        'data-no-payment': value ? (value.allowNoPayment ? 1 : 0) : 0,
      });
  }, [value]);

  useEffect(() => {
    if (orderLocation) {
      setValue(orderLocation);
    }
  }, [orderLocation]);

  return (
    <SelectForm
      isDisabled={!isEditMode}
      options={list}
      value={value}
      onChange={(v) => updateValueAndStorage(v)}
      getOptionLabel={(o) => o.name}
      getOptionValue={(o) => `${o.id}`}
      menuPortalTarget={dropdownProps?.menuPortalTarget}
      dataCy={dataCy}
      isLoading={isLoading}
      isClearable={clearable}
      styles={{
        input: (baseStyles) => ({
          ...baseStyles,
          padding: 0,
          margin: 0,
        }),
      }}
      placeholder="Select location"
    />
  );
};
